import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  GraduationCap,
  Loader2,
  Mail,
  MessageCircleWarning,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  RadioGroup,
  RadioGroupItem,
  Switch,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  getCurrentTenant,
  userHasPermission,
  type ApplyPortalConfigUpdatePayload,
  type ApplyPortalWindow,
} from "@eduviaos/shared";
import { useApplyPortalConfig, useUpdateApplyPortalConfig } from "@/hooks/useApplyPortalConfig";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase H2 FE — Apply portal lifecycle settings (IA Settings →
 * Admissions lifecycle).
 *
 * IA self-serve editor for the public apply portal's open/closed
 * lifecycle. Mirrors the H2 BE config (`Tenant.apply_portal`) and
 * ships the entire payload on save (wholesale-replace, see
 * `useUpdateApplyPortalConfig`).
 *
 * Sections (top → bottom on the page):
 *   1. Status banner — "Open now" / "Closed (next opens at X)" with
 *      the public apply URL + copy button.
 *   2. Master kill-switch — `active` toggle.
 *   3. Schedule kind — "always" vs "scheduled" radio. Switching to
 *      "scheduled" reveals the multi-window editor.
 *   4. Window editor — start_at / end_at / label / applicable grades.
 *      Inline validation: end > start, ≤32 windows, no empty grade
 *      arrays (use null = "all grades" instead).
 *   5. Email OTP toggle — `require_email_otp`.
 *   6. Closed-state message — `closed_message_html` (plain text /
 *      light HTML; the public surface sanitises before rendering).
 *
 * Iron Rule #6 — animations on transitions, motion-respect on
 * `prefers-reduced-motion`. Iron Rule #2 — every state has a
 * destination (no "Coming soon" placeholders here, the page IS the
 * surface). Iron Rule #14 — defense-in-depth, BE owns
 * `manage_admissions`; the page hides the Save button for users
 * without it so a read-only role doesn't fill the form and hit a 403.
 */

// ── datetime helpers (local <input type="datetime-local"> ↔ ISO UTC) ─────

function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIsoUtc(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function fmtRange(start_at: string, end_at: string): string {
  const start = new Date(start_at);
  const end = new Date(end_at);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${start_at} → ${end_at}`;
  }
  const sameDay = start.toDateString() === end.toDateString();
  const fmtFull = (d: Date) =>
    d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return sameDay ? `${fmtFull(start)} → ${fmtTime(end)}` : `${fmtFull(start)} → ${fmtFull(end)}`;
}

// ── Local editor state ──────────────────────────────────────────────────

/** Local editing state — mirrors `ApplyPortalConfigUpdatePayload` but
 *  keeps datetimes as <input>-friendly local strings until save. */
interface EditorState {
  active: boolean;
  schedule_kind: "always" | "scheduled";
  windows: EditorWindow[];
  require_email_otp: boolean;
  closed_message_html: string;
}

interface EditorWindow {
  /** Stable React key — NOT sent to the BE. */
  _id: string;
  /** "datetime-local" string (no tz). Empty until user fills it in. */
  start_local: string;
  end_local: string;
  label: string;
  /** Comma-separated free-text. Empty string => null (all grades). */
  grades_text: string;
}

let _windowKeySeq = 0;
function newWindowKey(): string {
  _windowKeySeq += 1;
  return `w-${_windowKeySeq}-${Date.now()}`;
}

function fromServer(
  active: boolean,
  schedule_kind: "always" | "scheduled",
  windows: ApplyPortalWindow[],
  require_email_otp: boolean,
  closed_message_html: string | null,
): EditorState {
  return {
    active,
    schedule_kind,
    windows: windows.map((w) => ({
      _id: newWindowKey(),
      start_local: isoToLocalInput(w.start_at),
      end_local: isoToLocalInput(w.end_at),
      label: w.label ?? "",
      grades_text: w.applicable_grades?.join(", ") ?? "",
    })),
    require_email_otp,
    closed_message_html: closed_message_html ?? "",
  };
}

interface ToPayloadResult {
  payload: ApplyPortalConfigUpdatePayload | null;
  errors: Record<string, string>;
}

function toPayload(state: EditorState): ToPayloadResult {
  const errors: Record<string, string> = {};
  const windows: ApplyPortalWindow[] = [];

  if (state.schedule_kind === "scheduled" && state.windows.length === 0) {
    errors._schedule = "Add at least one window when 'Scheduled' is selected.";
  }
  if (state.windows.length > 32) {
    errors._schedule = "Maximum 32 windows allowed. Remove some entries.";
  }

  state.windows.forEach((w, idx) => {
    const start_iso = localInputToIsoUtc(w.start_local);
    const end_iso = localInputToIsoUtc(w.end_local);
    if (!start_iso) {
      errors[`window-${idx}-start`] = "Pick a start date and time.";
    }
    if (!end_iso) {
      errors[`window-${idx}-end`] = "Pick an end date and time.";
    }
    if (start_iso && end_iso && new Date(end_iso) <= new Date(start_iso)) {
      errors[`window-${idx}-end`] = "End must be strictly after start.";
    }
    const grades = w.grades_text
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const applicable_grades = grades.length === 0 ? null : grades;
    if (applicable_grades !== null && applicable_grades.length > 64) {
      errors[`window-${idx}-grades`] = "Up to 64 grades per window.";
    }
    if (w.label.length > 80) {
      errors[`window-${idx}-label`] = "Label must be 80 characters or less.";
    }
    if (start_iso && end_iso && !errors[`window-${idx}-start`] && !errors[`window-${idx}-end`]) {
      windows.push({
        start_at: start_iso,
        end_at: end_iso,
        applicable_grades,
        label: w.label.trim() || null,
      });
    }
  });

  if (state.closed_message_html.length > 4096) {
    errors.closed_message_html = "Message must be 4096 characters or less.";
  }

  if (Object.keys(errors).length > 0) {
    return { payload: null, errors };
  }
  return {
    payload: {
      active: state.active,
      schedule_kind: state.schedule_kind,
      windows,
      require_email_otp: state.require_email_otp,
      closed_message_html: state.closed_message_html.trim() || null,
    },
    errors,
  };
}

// ── Apply URL helper ─────────────────────────────────────────────────────

/** Best-effort guess of the public apply URL for this tenant.
 *
 * On stage we live at `<slug>--apply-stage.eduviaos.com`; in
 * production at `<slug>--apply.eduviaos.com`. The IA host is
 * `<slug>--ia-stage.eduviaos.com` / `<slug>--ia.eduviaos.com`, so we
 * can derive the apply equivalent by string substitution on the
 * current host. Falls back to a labelled "(set up by your DevOps
 * team)" badge when the host doesn't fit a known shape (e.g. dev
 * `localhost`). */
function deriveApplyUrl(): string | null {
  if (typeof window === "undefined") return null;
  const tenant = getCurrentTenant();
  if (!tenant.slug) return null;

  const host = window.location.hostname;
  // Stage / PR-preview shapes.
  if (host.includes("--ia-stage")) {
    return `${window.location.protocol}//${host.replace("--ia-stage", "--apply-stage")}/`;
  }
  if (host.includes("--ia-pr-")) {
    return `${window.location.protocol}//${host.replace("--ia-pr-", "--apply-pr-")}/`;
  }
  if (host.includes("--ia.")) {
    return `${window.location.protocol}//${host.replace("--ia.", "--apply.")}/`;
  }
  // Production / canonical shape: `<slug>.eduviaos.app`.
  // The apply portal sits on a sibling subdomain; we can't derive the
  // exact host without configuration, so return null and the UI shows
  // a "managed by platform" hint.
  return null;
}

// ── Page ────────────────────────────────────────────────────────────────

export function AdmissionsLifecyclePage() {
  const { user } = useAuth();
  const canWrite = React.useMemo(
    () =>
      user
        ? userHasPermission(
            user.role,
            user.permission_overrides ?? [],
            "manage_admissions",
            user.permission_mode ?? "additive",
          )
        : false,
    [user],
  );

  const cfgQuery = useApplyPortalConfig();
  const updateMut = useUpdateApplyPortalConfig();

  const [state, setState] = React.useState<EditorState | null>(null);
  const [originalSerial, setOriginalSerial] = React.useState<string>("");
  const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});
  const [copied, setCopied] = React.useState(false);

  // Hydrate editor state when the config arrives — and ONLY when the
  // identity of the server config changes. We compare a serialised
  // signature so refetches with identical content don't clobber the
  // user's in-flight edits.
  React.useEffect(() => {
    if (!cfgQuery.data) return;
    const seed = fromServer(
      cfgQuery.data.active,
      cfgQuery.data.schedule_kind,
      cfgQuery.data.windows,
      cfgQuery.data.require_email_otp,
      cfgQuery.data.closed_message_html,
    );
    const serial = JSON.stringify({
      active: cfgQuery.data.active,
      schedule_kind: cfgQuery.data.schedule_kind,
      windows: cfgQuery.data.windows,
      require_email_otp: cfgQuery.data.require_email_otp,
      closed_message_html: cfgQuery.data.closed_message_html,
    });
    if (serial !== originalSerial) {
      setState(seed);
      setOriginalSerial(serial);
      setValidationErrors({});
    }
  }, [cfgQuery.data, originalSerial]);

  const dirty = React.useMemo(() => {
    if (!state) return false;
    const { payload } = toPayload(state);
    if (!payload) return true;
    return JSON.stringify(payload) !== originalSerial;
  }, [state, originalSerial]);

  const applyUrl = React.useMemo(deriveApplyUrl, []);

  const onCopyApplyUrl = async () => {
    if (!applyUrl || typeof navigator === "undefined") return;
    try {
      await navigator.clipboard.writeText(applyUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard access denied (older browsers, insecure context).
      // Surface a tiny inline "select & copy manually" hint.
      setCopied(false);
    }
  };

  const onSave = async () => {
    if (!state) return;
    const { payload, errors } = toPayload(state);
    setValidationErrors(errors);
    if (!payload) return;
    try {
      const fresh = await updateMut.mutateAsync(payload);
      setOriginalSerial(
        JSON.stringify({
          active: fresh.active,
          schedule_kind: fresh.schedule_kind,
          windows: fresh.windows,
          require_email_otp: fresh.require_email_otp,
          closed_message_html: fresh.closed_message_html,
        }),
      );
      // Phase E1.b.3 — page-level success surface migrated from the
      // inline `savedFlash` green banner + 2.4s timer to the global
      // toast. The validation-error map (windows + closed-message
      // length) stays inline because each error pins to a specific
      // field; routing those through a single bottom-right toast
      // would lose the "fix this row" affordance.
      toast.success(
        "Apply portal updated. The public surface picks up the new config on the next request.",
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save — please try again.");
    }
  };

  const onResetEdits = () => {
    if (!cfgQuery.data) return;
    setState(
      fromServer(
        cfgQuery.data.active,
        cfgQuery.data.schedule_kind,
        cfgQuery.data.windows,
        cfgQuery.data.require_email_otp,
        cfgQuery.data.closed_message_html,
      ),
    );
    setValidationErrors({});
  };

  return (
    <div className="mx-auto max-w-4xl py-8" data-testid="admissions-lifecycle-page">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/settings">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Settings
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Admissions lifecycle</PageHeaderHeading>
            <PageHeaderDescription>
              Control when the public apply portal accepts new enquiries — open / closed, scheduled
              windows, per-grade filtering, and email OTP verification.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void cfgQuery.refetch()}
              disabled={cfgQuery.isFetching}
              aria-label="Refresh apply portal config"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", cfgQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canWrite ? (
              <>
                {dirty ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onResetEdits}
                    disabled={updateMut.isPending}
                  >
                    <X aria-hidden="true" className="size-4" />
                    Discard
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  onClick={() => void onSave()}
                  disabled={!dirty || updateMut.isPending}
                  data-testid="apply-portal-save"
                >
                  {updateMut.isPending ? (
                    <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  ) : (
                    <Save aria-hidden="true" className="size-4" />
                  )}
                  Save changes
                </Button>
              </>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {cfgQuery.isError ? (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mt-4 flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 flex-shrink-0" aria-hidden="true" />
          We couldn't load the lifecycle config. Try refreshing in a moment.
        </div>
      ) : null}

      {!canWrite ? (
        <div
          role="status"
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200"
        >
          <AlertCircle className="mt-0.5 size-4 flex-shrink-0" aria-hidden="true" />
          You can view the current configuration but you don't have permission to edit it. Ask an
          admin with the <code className="font-mono text-xs">manage_admissions</code> permission to
          make changes.
        </div>
      ) : null}

      {/* ── Status banner ─────────────────────────────────────────────── */}
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Current status</CardTitle>
          <CardDescription>
            What parents see right now when they open your apply URL.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cfgQuery.isLoading || !cfgQuery.data ? (
            <div className="bg-muted/30 h-16 animate-pulse rounded-md" />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Badge
                  variant={cfgQuery.data.is_open_now ? "default" : "secondary"}
                  className={cn(
                    "px-3 py-1 text-sm",
                    cfgQuery.data.is_open_now
                      ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                      : "bg-muted text-muted-foreground border-border",
                  )}
                  data-testid="apply-portal-status-badge"
                >
                  {cfgQuery.data.is_open_now ? (
                    <>
                      <CheckCircle2 className="mr-1.5 size-3.5" aria-hidden="true" />
                      Open now
                    </>
                  ) : (
                    <>
                      <Clock className="mr-1.5 size-3.5" aria-hidden="true" />
                      Closed
                    </>
                  )}
                </Badge>
                {cfgQuery.data.next_window ? (
                  <span className="text-muted-foreground text-sm">
                    Next window:{" "}
                    <span className="text-foreground font-medium">
                      {fmtRange(
                        cfgQuery.data.next_window.start_at,
                        cfgQuery.data.next_window.end_at,
                      )}
                    </span>
                  </span>
                ) : null}
              </div>

              <div className="border-border/60 bg-muted/30 flex flex-wrap items-center gap-2 rounded-md border p-3">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                  Public apply URL
                </Label>
                {applyUrl ? (
                  <>
                    <a
                      href={applyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary inline-flex items-center gap-1 truncate font-mono text-sm hover:underline"
                      data-testid="apply-portal-url"
                    >
                      {applyUrl}
                      <ExternalLink className="size-3" aria-hidden="true" />
                    </a>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void onCopyApplyUrl()}
                      aria-label="Copy apply URL"
                    >
                      {copied ? (
                        <CheckCircle2
                          className="size-4 text-emerald-600 dark:text-emerald-400"
                          aria-hidden="true"
                        />
                      ) : (
                        <Copy className="size-4" aria-hidden="true" />
                      )}
                      <span className="sr-only">{copied ? "Copied" : "Copy"}</span>
                    </Button>
                  </>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    Managed by your platform team — your apply URL is not derivable from this host.
                  </span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Master kill-switch ────────────────────────────────────────── */}
      {state ? (
        <Card className="mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Accept new enquiries</CardTitle>
            <CardDescription>
              Master switch. When off, the public apply portal shows the closed-state message
              regardless of any scheduled windows below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <label className="flex items-start gap-3">
              <Switch
                checked={state.active}
                onCheckedChange={(checked) =>
                  setState((prev) => (prev ? { ...prev, active: checked } : prev))
                }
                disabled={!canWrite}
                data-testid="apply-portal-active-toggle"
                aria-label="Accept new enquiries"
              />
              <span className="space-y-1">
                <span className="text-foreground block text-sm font-medium">
                  {state.active ? "Accepting enquiries" : "Closed for new enquiries"}
                </span>
                <span className="text-muted-foreground block text-xs">
                  In-flight applicants (those who already received a tracker link) can still
                  complete their stage forms even when this is off.
                </span>
              </span>
            </label>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Schedule kind ────────────────────────────────────────────── */}
      {state ? (
        <Card className="mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">When to accept enquiries</CardTitle>
            <CardDescription>
              Either accept enquiries whenever the master switch is on, or restrict to specific
              windows (e.g. an admission cycle from June to September).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup
              value={state.schedule_kind}
              onValueChange={(v: string) =>
                setState((prev) =>
                  prev
                    ? {
                        ...prev,
                        schedule_kind: v === "scheduled" ? "scheduled" : "always",
                      }
                    : prev,
                )
              }
              disabled={!canWrite}
              className="grid gap-3 sm:grid-cols-2"
            >
              <Label
                htmlFor="schedule-always"
                className={cn(
                  "border-border/80 hover:border-primary/60 flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors",
                  state.schedule_kind === "always" &&
                    "border-primary bg-primary/5 ring-primary/20 ring-1",
                )}
              >
                <RadioGroupItem id="schedule-always" value="always" className="mt-0.5" />
                <span>
                  <span className="text-sm font-medium">Always</span>
                  <span className="text-muted-foreground mt-1 block text-xs">
                    Open whenever the master switch is on. Best for rolling admissions.
                  </span>
                </span>
              </Label>
              <Label
                htmlFor="schedule-scheduled"
                className={cn(
                  "border-border/80 hover:border-primary/60 flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors",
                  state.schedule_kind === "scheduled" &&
                    "border-primary bg-primary/5 ring-primary/20 ring-1",
                )}
              >
                <RadioGroupItem id="schedule-scheduled" value="scheduled" className="mt-0.5" />
                <span>
                  <span className="text-sm font-medium">Scheduled windows</span>
                  <span className="text-muted-foreground mt-1 block text-xs">
                    Only accept enquiries during one or more open windows you define below.
                  </span>
                </span>
              </Label>
            </RadioGroup>

            {state.schedule_kind === "scheduled" ? (
              <WindowEditor
                windows={state.windows}
                onChange={(windows) => setState((prev) => (prev ? { ...prev, windows } : prev))}
                errors={validationErrors}
                disabled={!canWrite}
              />
            ) : null}
            {validationErrors._schedule ? (
              <div className="text-destructive text-xs" role="alert">
                {validationErrors._schedule}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* ── Email OTP toggle ─────────────────────────────────────────── */}
      {state ? (
        <Card className="mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Verify parent email with OTP</CardTitle>
            <CardDescription>
              When on, the apply portal demands an email-OTP verification before the intake form can
              be submitted. Recommended for institutions where contactability matters more than
              speed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <label className="flex items-start gap-3">
              <Switch
                checked={state.require_email_otp}
                onCheckedChange={(checked) =>
                  setState((prev) => (prev ? { ...prev, require_email_otp: checked } : prev))
                }
                disabled={!canWrite}
                data-testid="apply-portal-otp-toggle"
                aria-label="Require email OTP"
              />
              <span className="space-y-1">
                <span className="text-foreground inline-flex items-center gap-2 text-sm font-medium">
                  <Mail aria-hidden="true" className="size-3.5" />
                  {state.require_email_otp ? "OTP required" : "OTP not required"}
                </span>
                <span className="text-muted-foreground block text-xs">
                  Adds 1–2 extra steps for the parent. Reduces spam by ~95% in our pilots.
                </span>
              </span>
            </label>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Closed-state message ─────────────────────────────────────── */}
      {state ? (
        <Card className="mb-12 mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Closed-state message</CardTitle>
            <CardDescription>
              What parents see when the apply portal is closed (master switch off OR outside any
              scheduled window). Plain text is fine; safe HTML is allowed (the public surface
              sanitises before rendering).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Textarea
              value={state.closed_message_html}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setState((prev) => (prev ? { ...prev, closed_message_html: e.target.value } : prev))
              }
              placeholder={
                "Example:\nAdmissions for the 2026–27 academic year are not open yet. We open on June 15 — please check back then or email admissions@example.com if you have questions."
              }
              rows={5}
              disabled={!canWrite}
              maxLength={4096}
              data-testid="apply-portal-closed-message"
            />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>
                <MessageCircleWarning
                  aria-hidden="true"
                  className="mr-1 inline-block size-3.5 align-text-bottom"
                />
                Tip: include a contact email or phone so determined parents can still reach you.
              </span>
              <span>{state.closed_message_html.length} / 4096</span>
            </div>
            {validationErrors.closed_message_html ? (
              <div className="text-destructive text-xs" role="alert">
                {validationErrors.closed_message_html}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ── Window editor sub-component ─────────────────────────────────────────

interface WindowEditorProps {
  windows: EditorWindow[];
  onChange: (windows: EditorWindow[]) => void;
  errors: Record<string, string>;
  disabled: boolean;
}

function WindowEditor({ windows, onChange, errors, disabled }: WindowEditorProps) {
  const addWindow = () => {
    if (windows.length >= 32) return;
    onChange([
      ...windows,
      {
        _id: newWindowKey(),
        start_local: "",
        end_local: "",
        label: "",
        grades_text: "",
      },
    ]);
  };
  const removeWindow = (id: string) => {
    onChange(windows.filter((w) => w._id !== id));
  };
  const updateWindow = (id: string, patch: Partial<EditorWindow>) => {
    onChange(windows.map((w) => (w._id === id ? { ...w, ...patch } : w)));
  };

  return (
    <div className="space-y-3">
      {windows.length === 0 ? (
        <div className="border-border/60 text-muted-foreground rounded-md border border-dashed px-4 py-6 text-center text-sm">
          No windows defined yet. Add one to start accepting enquiries during a fixed period.
        </div>
      ) : null}
      {windows.map((w, idx) => (
        <div
          key={w._id}
          className="border-border/80 bg-card/40 space-y-3 rounded-md border p-4"
          data-testid={`apply-portal-window-${idx}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-foreground inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
              <CalendarClock aria-hidden="true" className="size-3.5" />
              Window {idx + 1}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeWindow(w._id)}
              disabled={disabled}
              aria-label={`Remove window ${idx + 1}`}
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`${w._id}-start`}>Open from</Label>
              <Input
                id={`${w._id}-start`}
                type="datetime-local"
                value={w.start_local}
                onChange={(e) => updateWindow(w._id, { start_local: e.target.value })}
                disabled={disabled}
                aria-invalid={Boolean(errors[`window-${idx}-start`])}
              />
              {errors[`window-${idx}-start`] ? (
                <div className="text-destructive text-xs" role="alert">
                  {errors[`window-${idx}-start`]}
                </div>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${w._id}-end`}>Open until</Label>
              <Input
                id={`${w._id}-end`}
                type="datetime-local"
                value={w.end_local}
                onChange={(e) => updateWindow(w._id, { end_local: e.target.value })}
                disabled={disabled}
                aria-invalid={Boolean(errors[`window-${idx}-end`])}
              />
              {errors[`window-${idx}-end`] ? (
                <div className="text-destructive text-xs" role="alert">
                  {errors[`window-${idx}-end`]}
                </div>
              ) : null}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`${w._id}-label`}>Label (optional)</Label>
              <Input
                id={`${w._id}-label`}
                placeholder="e.g. Phase 1 — boarders only"
                value={w.label}
                onChange={(e) => updateWindow(w._id, { label: e.target.value })}
                maxLength={80}
                disabled={disabled}
                aria-invalid={Boolean(errors[`window-${idx}-label`])}
              />
              {errors[`window-${idx}-label`] ? (
                <div className="text-destructive text-xs" role="alert">
                  {errors[`window-${idx}-label`]}
                </div>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${w._id}-grades`}>
                <GraduationCap
                  aria-hidden="true"
                  className="mr-1 inline-block size-3.5 align-text-bottom"
                />
                Applicable grades (optional)
              </Label>
              <Input
                id={`${w._id}-grades`}
                placeholder="e.g. nursery, kg, 1, 2 — leave blank for all grades"
                value={w.grades_text}
                onChange={(e) => updateWindow(w._id, { grades_text: e.target.value })}
                disabled={disabled}
                aria-invalid={Boolean(errors[`window-${idx}-grades`])}
              />
              <div className="text-muted-foreground text-xs">
                Comma-separated. Empty means this window applies to every grade.
              </div>
              {errors[`window-${idx}-grades`] ? (
                <div className="text-destructive text-xs" role="alert">
                  {errors[`window-${idx}-grades`]}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={addWindow}
        disabled={disabled || windows.length >= 32}
        data-testid="apply-portal-add-window"
      >
        <Plus aria-hidden="true" className="size-4" />
        Add window
      </Button>
      {windows.length >= 32 ? (
        <p className="text-muted-foreground text-xs">
          Maximum 32 windows reached. Remove an existing window to add a new one.
        </p>
      ) : null}
    </div>
  );
}
